import Phaser from "phaser";
import { resolveColour } from "odyssey-shared";
import { pixelTextWidth } from "../../../ui/kit/pixel-text.ts";
import { pageKeys, type KeyTable } from "../../controls/page.ts";
import type { GridCamera } from "../../world-zoom.ts";
import { UI_ATLAS_KEY } from "../assets.ts";
import type { WorldUiContext, WorldUiModule } from "../hook.ts";
import { renderedView } from "../mount.ts";
import { PIXEL_FONT_KEY, PLAIN_FONT_KEY, pixelFontSize } from "../pixel-font.ts";
import { projectToDevice, type MutablePoint } from "../projection.ts";
import { verbLabel } from "./label.ts";
import { pageKeyHints, type KeyHints } from "../../touch/key-hints.ts";
import { KEY_HEIGHT, keyCapWidth, newPromptLayout, promptBelow, promptHit, promptLayout, type PromptLayout } from "./layout.ts";
import type { PromptReading, PromptReport } from "./report.ts";
import { GREYED_INK, GREYED_KEY_TINT } from "./tones.ts";

/**
 * The interaction prompt on the canvas (P1.9-T17; ADR-061 decisions 1 to 3 and its amendments; ADR-051, ADR-053,
 * ADR-058): while the thing E would act on is in reach, a corner bracket marks its art and, beside it, the E key cap
 * and the verb its state offers ("Read", "Lower", "Raise", "Open", "Close"), on the side of the thing away from the
 * character. Nothing in reach, nothing drawn, and E still says "Nothing to use here."; a refusal stays on the message
 * line, but for a lock, which the prompt draws (below). The target is the scene's, from the lookup E shares (`interact-target.ts`), so the prompt names exactly what
 * E acts on, and with two usable things in reach it names the first the view holds, as E acts on it. A presence type
 * such as the Roof offers no verb and gets no prompt.
 *
 * The UI camera draws it, so a prompt pixel is a HUD pixel: the bracket is the atlas's `ui/frame/bracket`, original art
 * drawn as a grid, as a `NineSlice` stretched over the art's box; the key cap is the kit's key glyph, the atlas's key
 * frame as a `NineSlice` with the key's legend on it, as the HUD's Esc glyph is drawn in the DOM: the key is the one
 * the page's key table binds Interact to (P1.9-T50, `controls/`), its legend the atlas's `ui/key/e` while that is E
 * (the pixel face's glyph without the bitmap font's outline) and, for any other key, the key's label as a
 * `BitmapText` in the plain pixel face, which has no outline either, in the same colour, the cap as wide as its
 * legend. The legend and the cap follow the table when it changes, once, and never on a frame; the label is one `BitmapText` in the pixel face at 1x in the text
 * colour. The label is, provisionally, the contract verb with underscores as spaces in sentence case (`label.ts`).
 *
 * It changes only when the target or the verb does: its label is set only when the verb changes, its parts are laid
 * out only when the art's box, the UI scale or the side moves, and they are made once, when a thing is first in reach.
 * A frame of steady play asks the lookup, which compares four cells and returns the same target, projects two
 * corners into points made once and allocates nothing.
 *
 * While the key is withheld (`TargetSource.withheld`: the character is stunned, and the map server refuses its
 * interact, ADR-079's amendment of 2026-10-07, point 5) the prompt is drawn greyed, the design's `greyed` state: the
 * bracket, the thing and its verb still named, the key cap's fill and the label's ink at `vellum-400` (`tones.ts` has
 * why the label is not the design's `vellum-500`), so the key is plainly not offered. The tones are set only when that
 * changes.
 *
 * While the thing named refused the verb it offers (`TargetSource.refusal`, a locked gate, P1.8-T4) the prompt is drawn
 * in the design's `refused` state: the row in the offer's tones and, under it, the atlas's 8 px close glyph and the
 * reason in the pixel face at the label's size, both in the danger colour (`layout.ts` places them). Its two objects
 * are made the first time a reason is shown, and its text is set only when the reason changes.
 */

/**
 * The corner bracket in the UI atlas.
 */
export const BRACKET_FRAME = "ui/frame/bracket";

/**
 * The key frame in the UI atlas, the kit's key glyph's.
 */
export const KEY_FRAME = "ui/frame/key";

/**
 * The legend the atlas holds as art: E, Interact's key before a player chooses another.
 */
export const KEY_LEGEND = "E";

/**
 * The key's legend in the UI atlas.
 */
export const LEGEND_FRAME = "ui/key/e";

/**
 * The refusal's close glyph in the UI atlas, the 8 px close the design's refused state draws.
 */
export const REASON_FRAME = "ui/glyph/close-small_8";

/**
 * The refusal's ink as a tint: the danger colour.
 */
const DANGER = Number.parseInt(resolveColour("danger").slice(1), 16);

/**
 * A text legend's ink as a tint: the colour of text on a light fill, the atlas legend's.
 */
const LEGEND_INK = Number.parseInt(resolveColour("text-on-light").slice(1), 16);

/**
 * The label's ink as a tint: the text colour, as the HUD's labels.
 */
const INK = Number.parseInt(resolveColour("text").slice(1), 16);

/**
 * The depth of every prompt object in the UI layer, over the nameplates at 0, the chat bubbles at 1 and the emoji over
 * characters at 2, so an emoji never covers the key E acts by.
 */
export const PROMPT_DEPTH = 3;

/**
 * The prompt's display objects: the bracket, the key cap, its legend and the label, and a refusal's glyph and text
 * once one has been shown.
 */
interface PromptObjects {
  readonly bracket: Phaser.GameObjects.NineSlice;
  readonly key: Phaser.GameObjects.NineSlice;
  legend: Phaser.GameObjects.Image | Phaser.GameObjects.BitmapText;
  readonly label: Phaser.GameObjects.BitmapText;
  reason: { readonly glyph: Phaser.GameObjects.Image; readonly text: Phaser.GameObjects.BitmapText } | null;
}

/**
 * A box in whole device pixels, written in place.
 */
interface MutableBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Everything the prompt keeps between frames: its context and report, the layout, the art's box and the points it
 * writes in place, the key table, the key's legend and the key cap's width for it, its objects once made, and what
 * they show, in which tones and where, so a frame that changes nothing touches nothing.
 */
interface PromptState {
  readonly context: WorldUiContext;
  readonly report: PromptReport;
  readonly layout: PromptLayout;
  readonly art: MutableBox;
  readonly view: { -readonly [K in keyof GridCamera]: GridCamera[K] };
  readonly corner: MutablePoint;
  readonly feet: MutablePoint;
  readonly keys: KeyTable;
  readonly hints: Pick<KeyHints, "getSnapshot">;
  keyShown: boolean;
  legend: string;
  keyWidth: number;
  objects: PromptObjects | null;
  verb: string;
  label: string;
  labelWidth: number;
  scale: number;
  below: boolean;
  placed: boolean;
  shown: boolean;
  greyed: boolean;
  reason: string;
}

/**
 * Brings the prompt up to date for a frame: hidden with the switch off or nothing in reach; otherwise its objects made
 * if this is the first target, its label set if the verb changed, its tones set if the key is newly withheld or
 * offered again, its parts laid out if the art's box, the UI scale or the side moved, and shown.
 *
 * @param state The prompt.
 */
function promptFrame(state: PromptState): void {
  const context = state.context;
  const report = state.report;
  report.scans = context.target.scanCount();
  if (!report.enabled) {
    hidePrompt(state);
    return;
  }
  const target = context.target.current();
  if (!target.found) {
    hidePrompt(state);
    return;
  }
  const objects = state.objects ?? makeObjects(state);
  if (target.verb !== state.verb) {
    showVerb(state, objects, target.verb);
  }
  const greyed = context.target.withheld();
  if (greyed !== state.greyed) {
    tonePrompt(state, objects, greyed);
  }
  const reason = context.target.refusal(target.handle, target.verb);
  if (reason !== state.reason) {
    showReason(state, objects, reason);
  }
  report.handle = target.handle;
  report.verb = state.verb;
  report.label = state.label;
  report.greyed = greyed;
  report.reason = reason;
  const keyShown = state.hints.getSnapshot();
  if (keyShown !== state.keyShown) {
    state.keyShown = keyShown;
    state.placed = false;
    objects.key.setVisible(state.shown && keyShown);
    objects.legend.setVisible(state.shown && keyShown);
  }
  const scale = context.scale.current;
  const corner = state.corner;
  const view = renderedView(context.worldCamera, state.view);
  projectToDevice(view, target.left, target.top, corner);
  const left = corner.x;
  const top = corner.y;
  projectToDevice(view, target.right, target.bottom, corner);
  const right = corner.x;
  const bottom = corner.y;
  const below = context.characters.local(state.feet) && promptBelow(target.top, target.bottom, state.feet.y);
  const art = state.art;
  if (!state.placed || left !== art.left || top !== art.top || right !== art.right || bottom !== art.bottom || scale !== state.scale || below !== state.below) {
    art.left = left;
    art.top = top;
    art.right = right;
    art.bottom = bottom;
    state.scale = scale;
    state.below = below;
    state.placed = true;
    placePrompt(state, objects);
  }
  if (!state.shown) {
    objects.bracket.setVisible(true);
    objects.key.setVisible(state.keyShown);
    objects.legend.setVisible(state.keyShown);
    objects.label.setVisible(true);
    objects.reason?.glyph.setVisible(state.reason !== "");
    objects.reason?.text.setVisible(state.reason !== "");
    state.shown = true;
  }
  report.shown = true;
}

/**
 * Hides the prompt, if shown, and reports nothing named.
 *
 * @param state The prompt.
 */
function hidePrompt(state: PromptState): void {
  const report = state.report;
  report.shown = false;
  report.handle = -1;
  report.verb = "";
  report.label = "";
  report.greyed = false;
  report.reason = "";
  const objects = state.objects;
  if (!state.shown || objects === null) {
    return;
  }
  objects.bracket.setVisible(false);
  objects.key.setVisible(false);
  objects.legend.setVisible(false);
  objects.label.setVisible(false);
  objects.reason?.glyph.setVisible(false);
  objects.reason?.text.setVisible(false);
  state.shown = false;
}

/**
 * Sets the refusal drawn under the row, making its glyph and text the first time one is shown, and asks for the parts
 * to be laid out again, since a reason over the bracket lifts the row.
 *
 * @param state The prompt.
 * @param objects Its objects.
 * @param reason The reason, or "" for none.
 */
function showReason(state: PromptState, objects: PromptObjects, reason: string): void {
  if (reason !== "" && objects.reason === null) {
    const context = state.context;
    const glyph = context.add(new Phaser.GameObjects.Image(context.scene, 0, 0, UI_ATLAS_KEY, REASON_FRAME));
    const text = context.add(new Phaser.GameObjects.BitmapText(context.scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("label")));
    glyph.setOrigin(0, 0).setDepth(PROMPT_DEPTH).setTint(DANGER).setVisible(false);
    text.setOrigin(0, 0).setDepth(PROMPT_DEPTH).setTint(DANGER).setVisible(false);
    objects.reason = { glyph, text };
  }
  if (objects.reason !== null) {
    objects.reason.text.setText(reason);
    objects.reason.glyph.setVisible(state.shown && reason !== "");
    objects.reason.text.setVisible(state.shown && reason !== "");
  }
  state.reason = reason;
  state.placed = false;
}

/**
 * Sets the label for a new verb and asks for the parts to be laid out again, since the label's width moves them.
 *
 * @param state The prompt.
 * @param objects Its objects.
 * @param verb The verb the target offers now.
 */
function showVerb(state: PromptState, objects: PromptObjects, verb: string): void {
  const label = verbLabel(verb);
  objects.label.setText(label);
  state.verb = verb;
  state.label = label;
  state.labelWidth = pixelTextWidth(label);
  state.placed = false;
  state.report.labels += 1;
}

/**
 * Sets the prompt's tones: greyed while the key is withheld, the key cap's fill and the label's ink at `vellum-400`,
 * and the offer's own otherwise. The legend keeps its ink, as the design's greyed key does.
 *
 * @param state The prompt.
 * @param objects Its objects.
 * @param greyed Whether the key is withheld.
 */
function tonePrompt(state: PromptState, objects: PromptObjects, greyed: boolean): void {
  if (greyed) {
    objects.key.setTint(GREYED_KEY_TINT);
  } else {
    objects.key.clearTint();
  }
  objects.label.setTint(greyed ? GREYED_INK : INK);
  state.greyed = greyed;
}

/**
 * Lays the prompt's parts out over the art's box and moves them there; the bracket is sized only when its size moves.
 *
 * @param state The prompt.
 * @param objects Its objects.
 */
function placePrompt(state: PromptState, objects: PromptObjects): void {
  const layout = promptLayout(state.art, state.scale, state.keyWidth, state.labelWidth, state.below, 0, state.layout, state.reason !== "", state.keyShown);
  const bracket = objects.bracket;
  if (bracket.width !== layout.bracketWidth || bracket.height !== layout.bracketHeight) {
    bracket.setSize(layout.bracketWidth, layout.bracketHeight);
  }
  bracket.setPosition(layout.bracketX, layout.bracketY);
  objects.key.setPosition(layout.keyX, layout.keyY);
  objects.legend.setPosition(layout.legendX, layout.legendY);
  objects.label.setPosition(layout.labelX, layout.labelY);
  objects.reason?.glyph.setPosition(layout.reasonGlyphX, layout.reasonGlyphY);
  objects.reason?.text.setPosition(layout.reasonX, layout.reasonY);
}

/**
 * Makes the key's legend in the UI layer, hidden: the atlas's art for E, or the key's label in the plain pixel face.
 *
 * @param state The prompt, whose legend it draws.
 * @returns The legend.
 */
function makeLegend(state: PromptState): Phaser.GameObjects.Image | Phaser.GameObjects.BitmapText {
  const context = state.context;
  const scene = context.scene;
  const legend = state.legend === KEY_LEGEND ? context.add(new Phaser.GameObjects.Image(scene, 0, 0, UI_ATLAS_KEY, LEGEND_FRAME)) : context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PLAIN_FONT_KEY, state.legend, pixelFontSize("label")).setTint(LEGEND_INK));
  legend.setOrigin(0, 0).setDepth(PROMPT_DEPTH).setVisible(false);
  return legend;
}

/**
 * Takes the key Interact is bound to now: the legend, the cap's width for it and, when the objects are made, a cap of
 * that width and a new legend in the old one's place, laid out on the next frame that shows the prompt.
 *
 * @param state The prompt.
 */
function relabel(state: PromptState): void {
  const legend = state.keys.label("interact");
  if (legend === state.legend) {
    return;
  }
  state.legend = legend;
  state.keyWidth = keyCapWidth(pixelTextWidth(legend));
  state.placed = false;
  const objects = state.objects;
  if (objects === null) {
    return;
  }
  objects.key.setSize(state.keyWidth, KEY_HEIGHT);
  objects.legend.destroy();
  objects.legend = makeLegend(state);
  objects.legend.setVisible(state.shown && state.keyShown);
}

/**
 * Makes the prompt's objects in the UI layer, hidden, the first time a thing is in reach.
 *
 * @param state The prompt.
 * @returns The objects.
 */
function makeObjects(state: PromptState): PromptObjects {
  const context = state.context;
  const scene = context.scene;
  const bracket = context.add(new Phaser.GameObjects.NineSlice(scene, 0, 0, UI_ATLAS_KEY, BRACKET_FRAME, 10, 10));
  const key = context.add(new Phaser.GameObjects.NineSlice(scene, 0, 0, UI_ATLAS_KEY, KEY_FRAME, state.keyWidth, KEY_HEIGHT));
  const legend = makeLegend(state);
  const label = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("label")));
  bracket.setOrigin(0, 0).setDepth(PROMPT_DEPTH).setVisible(false);
  key.setOrigin(0, 0).setDepth(PROMPT_DEPTH).setVisible(false);
  label.setOrigin(0, 0).setDepth(PROMPT_DEPTH).setTint(INK).setVisible(false);
  const objects: PromptObjects = { bracket, key, legend, label, reason: null };
  state.objects = objects;
  return objects;
}

/**
 * Reads the prompt for the report: made on request, never on a frame.
 *
 * @param state The prompt.
 * @returns Its reading, or null before a thing was first in reach.
 */
function readPrompt(state: PromptState): PromptReading | null {
  const objects = state.objects;
  if (objects === null) {
    return null;
  }
  const layer = state.context.layer;
  return {
    handle: state.report.handle,
    verb: state.verb,
    label: objects.label.text,
    shown: state.shown,
    greyed: state.greyed,
    keyTint: objects.key.tint,
    labelTint: objects.label.tintTopLeft,
    below: state.below,
    scale: state.scale,
    bracketX: objects.bracket.x,
    bracketY: objects.bracket.y,
    bracketWidth: objects.bracket.width,
    bracketHeight: objects.bracket.height,
    keyX: objects.key.x,
    keyY: objects.key.y,
    keyWidth: state.keyWidth,
    keyShown: state.keyShown,
    legend: state.legend,
    legendX: objects.legend.x,
    legendY: objects.legend.y,
    labelX: objects.label.x,
    labelY: objects.label.y,
    labelWidth: state.labelWidth,
    reason: objects.reason === null ? "" : objects.reason.text.text,
    reasonX: objects.reason === null ? 0 : objects.reason.text.x,
    reasonY: objects.reason === null ? 0 : objects.reason.text.y,
    reasonTint: objects.reason === null ? 0 : objects.reason.text.tintTopLeft,
    objects: objects.reason === null ? 4 : 6,
    inUiLayer: objects.bracket.displayList === layer && objects.key.displayList === layer && objects.legend.displayList === layer && objects.label.displayList === layer && (objects.reason === null || (objects.reason.glyph.displayList === layer && objects.reason.text.displayList === layer)),
  };
}

/**
 * The interaction prompt's surface.
 *
 * @param report The game report's prompt entry: its switch, and where what it draws is reported.
 * @param keys The key table, whose Interact key the cap names; the page's unless given.
 * @param hints Whether a key may be named (`touch/key-hints.ts`): without it the cap and its legend are not drawn and the
 *   row is the verb alone; the page's unless given.
 * @returns The surface's module, listed after the chat bubbles.
 */
export function prompt(report: PromptReport, keys: KeyTable = pageKeys, hints: Pick<KeyHints, "getSnapshot"> = pageKeyHints): WorldUiModule {
  return {
    id: "prompt",
    /**
     * Keeps the prompt's state, making no object until a thing is first in reach, and reads it for the report.
     *
     * @param context The UI layer, the projection, the characters the scene draws and the thing E acts on.
     * @returns The surface.
     */
    create(context) {
      const legend = keys.label("interact");
      const state: PromptState = {
        context,
        report,
        layout: newPromptLayout(),
        art: { left: 0, top: 0, right: 0, bottom: 0 },
        view: { width: 0, height: 0, originX: 0, originY: 0, zoom: 1, scrollX: 0, scrollY: 0 },
        corner: { x: 0, y: 0 },
        feet: { x: 0, y: 0 },
        keys,
        hints,
        keyShown: hints.getSnapshot(),
        legend,
        keyWidth: keyCapWidth(pixelTextWidth(legend)),
        objects: null,
        verb: "",
        label: "",
        labelWidth: 0,
        scale: 0,
        below: false,
        placed: false,
        shown: false,
        greyed: false,
        reason: "",
      };
      /**
       * Reads the prompt.
       *
       * @returns Its reading.
       */
      report.read = (): PromptReading | null => readPrompt(state);
      /**
       * Whether a press, in device pixels, lands on the prompt now.
       *
       * @param x The press's x.
       * @param y The press's y.
       * @returns True while the prompt is shown and the press is on it.
       */
      report.hit = (x: number, y: number): boolean => state.shown && state.placed && !state.greyed && promptHit(state.layout, state.scale, x, y);
      const stop = keys.subscribe(() => relabel(state));
      return {
        /**
         * Brings the prompt up to date for the frame.
         */
        update() {
          promptFrame(state);
        },
        /**
         * Stops following the key table and forgets the report's reading, which would read the destroyed objects.
         */
        destroy() {
          stop();
          /**
           * Reads nothing, since the prompt's objects went with the scene.
           *
           * @returns Nothing.
           */
          report.read = (): PromptReading | null => null;
          report.hit = (): boolean => false;
        },
      };
    },
  };
}
